import { useCallback, useState } from 'react';
import { useAudioLevelObserver, useParticipantProperty } from '@daily-co/daily-react';
import { Mic as MicIcon, MicOff as MicOffIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

export const pillBadgeSx = {
  position: 'absolute',
  bottom: 4,
  left: 4,
  bgcolor: 'rgba(0,0,0,0.6)',
  color: 'common.white',
  borderRadius: '5px',
  px: 0.75,
  py: 0.25,
} as const;

export const iconOnlySx = {
  position: 'absolute',
  bottom: 4,
  left: 4,
  color: 'common.white',
} as const;

export const centeredNameSx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

export function MicIndicator({ enabled, fontSize = 14 }: { enabled: boolean; fontSize?: number }) {
  return enabled ? <MicIcon sx={{ fontSize }} /> : <MicOffIcon sx={{ fontSize }} />;
}

const LEVEL_BAR_COUNT = 3;

/**
 * Mic icon with real-time audio level bars for a participant.
 * Uses Daily's useAudioLevelObserver so it works for both local and remote participants.
 */
export function MicLevelIndicator({
  sessionId,
  enabled,
  fontSize = 14,
}: {
  sessionId: string;
  enabled: boolean;
  fontSize?: number;
}) {
  const [level, setLevel] = useState(0);

  useAudioLevelObserver(
    sessionId,
    useCallback((volume: number) => {
      setLevel(volume ?? 0);
    }, []),
  );

  if (!enabled) {
    return <MicOffIcon sx={{ fontSize }} />;
  }

  // Daily's raw volume is very low for normal speech (~0.01-0.15).
  // Boost gain (3.5x) and use a sqrt curve so soft speech already
  // lights the first bar and normal conversation reaches 2-3 bars.
  const amplified = Math.min(1, Math.pow((level || 0) * 3.5, 0.5));
  const activeBars = Math.round(amplified * LEVEL_BAR_COUNT);
  const barHeight = fontSize * 0.5;
  const barWidth = 2.5;
  const barGap = 1;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      <MicIcon sx={{ fontSize }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: `${barGap}px`,
          height: fontSize,
        }}
        aria-label="Audio level"
        role="meter"
        aria-valuenow={Math.round(amplified * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {Array.from({ length: LEVEL_BAR_COUNT }, (_, i) => {
          const scale = 0.4 + (i / (LEVEL_BAR_COUNT - 1)) * 0.6;
          const isLit = i < activeBars;
          return (
            <Box
              key={i}
              sx={{
                width: barWidth,
                height: barHeight * scale,
                borderRadius: '1px',
                bgcolor: isLit ? 'success.light' : 'rgba(255,255,255,0.3)',
                transition: 'background-color 80ms linear',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export function CenteredName({ name }: { name: string }) {
  return (
    <Box sx={centeredNameSx}>
      <Typography variant="body1" sx={{ color: 'common.white', fontWeight: 500 }}>
        {name}
      </Typography>
    </Box>
  );
}

interface ParticipantBadgeProps {
  sessionId: string;
  isLocal?: boolean;
  /** Pass explicitly when parent already knows video state. If omitted, reads from useParticipantProperty. */
  videoEnabled?: boolean;
  /** Pass explicitly for local participant when audio state is lifted. If omitted, reads from useParticipantProperty. */
  audioEnabled?: boolean;
}

export function ParticipantBadge({
  sessionId,
  isLocal = false,
  videoEnabled,
  audioEnabled,
}: ParticipantBadgeProps) {
  const name = useParticipantProperty(sessionId, 'user_name');
  const participantAudio = useParticipantProperty(sessionId, 'audio');
  const participantVideo = useParticipantProperty(sessionId, 'video');

  const audio = audioEnabled ?? participantAudio;
  const video = videoEnabled ?? participantVideo;
  const displayName = isLocal ? (name ? `${name} (You)` : 'You') : name || 'Guest';

  if (!video) {
    return (
      <Box sx={iconOnlySx}>
        <MicLevelIndicator sessionId={sessionId} enabled={audio} />
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={[{ alignItems: "center" }, pillBadgeSx]}>
      <MicLevelIndicator sessionId={sessionId} enabled={audio} />
      <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1 }}>
        {displayName}
      </Typography>
    </Stack>
  );
}
